/**
 * @fileoverview Shared types for the host-configurable companion prompt
 *   override + adult-mode feature (GitHub issue #1663).
 *
 * These types are consumed by both the web-next host API (packages/web-next)
 * and the host-facing UI components. The resolver in the config service
 * (packages/api.telegamez.com) is authoritative for runtime precedence;
 * these types shape the host-visible read/write API.
 */
import type { CompanionTemplateBlanks } from './CompanionTemplates';

/**
 * Which precedence layer resolved the effective system prompt.
 *   - `'room'`: host-defined override on this (workflow, companion) pair: an approved template
 *     with the host's blanks filled, or free text written before templates replaced it.
 *   - `'admin'`: global `avatar_companions.customPrompt` set by a platform admin.
 *   - `'template'`: auto-generated from the companion's personality/expertise/context.
 */
export type EffectivePromptSource = 'room' | 'admin' | 'template';

/**
 * Which precedence layer resolved the effective TTS voice for a companion:
 *   - `'room'`: host-defined voice override on this (workflow, companion).
 *   - `'companion'`: companion's default voice from `avatar_companions.voiceId`.
 */
export type EffectiveVoiceSource = 'room' | 'companion';

/**
 * Which precedence layer resolved the effective display name for a companion:
 *   - `'room'`: host-defined name override on this (workflow, companion).
 *   - `'companion'`: companion's default name from `avatar_companions.name`.
 */
export type EffectiveNameSource = 'room' | 'companion';

/** Server-enforced max length for a host-set custom companion name. */
export const CUSTOM_NAME_MAX_LENGTH = 30;

/**
 * Server-enforced character policy for a host-set custom companion name.
 *
 * Allowed: letters (any script), numbers, ASCII spaces, hyphen, apostrophe,
 * period, underscore. Rejects control characters, newlines, quotes, backslashes,
 * and anything else that could escape out of the identity line injection in
 * `prependIdentityLine` (`Your name is "${name}".`) and hand a host a
 * prompt-injection surface.
 *
 * `u` flag enables Unicode property escapes so non-English names work
 * (e.g. "Søren", "佐藤", "Zoë").
 */
export const CUSTOM_NAME_ALLOWED_CHARS = /^[\p{L}\p{N} '\-._]+$/u;

/**
 * Validates a host-submitted custom companion name. Returns `null` when OK,
 * otherwise a human-readable reason. Callers pair this with the
 * `INVALID_CUSTOM_NAME` error code.
 *
 * `null` / empty / whitespace-only are treated as "clear the override" by the
 * PUT route and are not validated here — callers should short-circuit that
 * case before calling.
 */
export function validateCustomName(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'name is empty';
  if (trimmed.length > CUSTOM_NAME_MAX_LENGTH) {
    return `name exceeds maximum length of ${CUSTOM_NAME_MAX_LENGTH} characters`;
  }
  if (!CUSTOM_NAME_ALLOWED_CHARS.test(trimmed)) {
    return 'name contains disallowed characters (use letters, numbers, spaces, hyphens, apostrophes, periods)';
  }
  return null;
}

/**
 * Voice provider that may be stored in `room_companion_prompt_overrides.voice_provider`.
 * Phase 7 ships Inworld-only host overrides; `'cartesia'` remains for legacy rows.
 * Hosts cannot set anything else via the UI — see `HOST_ALLOWED_PROVIDERS` in
 * the override route for the runtime whitelist.
 */
export type VoiceOverrideProvider = 'inworld' | 'cartesia';

/**
 * Provider of an _effective_ (resolved) voice. Wider than `VoiceOverrideProvider`
 * because a companion's default voice may be an OpenAI named voice (`alloy`,
 * `nova`, …) even though hosts can't pick OpenAI for an override.
 */
export type EffectiveVoiceProvider = VoiceOverrideProvider | 'openai';

/**
 * Error codes returned by the companion-prompt-override and
 * adult-content-acknowledgement endpoints. Clients should prefer these over
 * parsing error messages.
 */
export const CompanionPromptOverrideErrorCode = {
  PROMPT_TOO_LARGE: 'PROMPT_TOO_LARGE',
  HOST_OVERRIDE_DISABLED: 'HOST_OVERRIDE_DISABLED',
  COMPANION_NOT_ADULT_CAPABLE: 'COMPANION_NOT_ADULT_CAPABLE',
  ADULT_ACKNOWLEDGEMENT_REQUIRED: 'ADULT_ACKNOWLEDGEMENT_REQUIRED',
  ADULT_MODE_DISABLED: 'ADULT_MODE_DISABLED',
  NOT_AUTHORIZED: 'NOT_AUTHORIZED',
  INVALID_COMPANION: 'INVALID_COMPANION',
  INVALID_ROOM: 'INVALID_ROOM',
  INVALID_VOICE_PROVIDER: 'INVALID_VOICE_PROVIDER',
  INVALID_VOICE_ID: 'INVALID_VOICE_ID',
  INVALID_CUSTOM_NAME: 'INVALID_CUSTOM_NAME',
  /** The template id is not one of the approved templates. */
  INVALID_TEMPLATE: 'INVALID_TEMPLATE',
  /** A word in a template blank breaks the blank rules; `blanks` says which. */
  INVALID_TEMPLATE_BLANK: 'INVALID_TEMPLATE_BLANK',
  /** The body (avatar) the host chose is not an active avatar. */
  INVALID_AVATAR: 'INVALID_AVATAR',
  /** Free-text prompts can no longer be written; pick a template. Clearing old free text is still allowed. */
  FREE_TEXT_RETIRED: 'FREE_TEXT_RETIRED',
  COMPANION_NOT_FOUND: 'COMPANION_NOT_FOUND',
  WORKFLOW_NOT_FOUND: 'WORKFLOW_NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

export type CompanionPromptOverrideErrorCode =
  (typeof CompanionPromptOverrideErrorCode)[keyof typeof CompanionPromptOverrideErrorCode];

// ---------------------------------------------------------------------------
// GET /api/rooms/[roomId]/companion-prompt-override?companionId=...
// ---------------------------------------------------------------------------

export interface CompanionPromptOverrideGetResponse {
  /**
   * Free text written before templates replaced it, or null. Still applied until the host picks a
   * template or clears it; it can no longer be edited.
   */
  override: string | null;
  /** The approved template the prompt is built from, or null. */
  templateId: string | null;
  /** The host's words for the template's blanks. */
  templateBlanks: CompanionTemplateBlanks | null;
  /** Whether the host has toggled adult mode on for this (room, companion). */
  adultModeEnabled: boolean;
  /**
   * The prompt that will actually be applied for this companion in this room
   * given current precedence — suitable to pre-fill the host editor as a
   * starting point when creating a new override.
   */
  effectivePrompt: string;
  /** Which layer `effectivePrompt` was resolved from. */
  source: EffectivePromptSource;
  /** `true` when the platform kill-switch disables host overrides globally. */
  platformDisabled: boolean;
  /** `true` when the platform kill-switch disables adult mode globally. */
  adultModeDisabled: boolean;
  /** Server-enforced maximum override length in bytes. */
  sizeCapBytes: number;
  /** Admin whitelist: whether hosts may opt this companion into adult mode. */
  companionAdultCapable: boolean;
  /** Workflow-level adult policy acknowledgement (Security tab). */
  acknowledged: boolean;
  /** ISO timestamp when the acknowledgement was set (null if not acknowledged). */
  acknowledgedAt: string | null;
  /** Host-set voice override id (provider-scoped), or null when no override. */
  voiceOverrideId: string | null;
  /** Provider of the host override (matches the override id). */
  voiceOverrideProvider: VoiceOverrideProvider | null;
  /** The voice that will actually be used — override when set, else companion default. */
  effectiveVoiceId: string;
  /** Provider of `effectiveVoiceId` (detected from id format when coming from the companion default). */
  effectiveVoiceProvider: EffectiveVoiceProvider;
  /** Which layer `effectiveVoiceId` was resolved from. */
  voiceSource: EffectiveVoiceSource;
  /** The companion's configured default voice id (for "Reset to default" UI). */
  companionVoiceId: string;
  /** Provider of the companion default voice id. */
  companionVoiceProvider: EffectiveVoiceProvider;
  /** Host-chosen body (an avatar id) shown instead of the companion's own, or null for the companion's own. */
  bodyOverrideAvatarId: string | null;
  /** The avatar the companion wears by default, for "Reset to default" and the picker's starting point. */
  companionAvatarId: string | null;
  /** Host-set name override, or null when no override. */
  customName: string | null;
  /** The name that will actually be used — override when set, else companion default. */
  effectiveName: string;
  /** Which layer `effectiveName` was resolved from. */
  nameSource: EffectiveNameSource;
  /** The companion's default name (from `avatar_companions.name`) — for "Reset to default" UI. */
  companionDefaultName: string;
}

// ---------------------------------------------------------------------------
// PUT /api/rooms/[roomId]/companion-prompt-override
// ---------------------------------------------------------------------------

export interface CompanionPromptOverridePutBody {
  companionId: string;
  /**
   * Partial-update semantics: fields that are `undefined` are not modified.
   * Passing `voiceOverrideId: null` clears the voice portion. The row is
   * deleted only when every override field is empty AND adult mode is off
   * after the merge.
   *
   * Free text is retired: only `customPrompt: ""` (clear) is accepted, and
   * anything else is `FREE_TEXT_RETIRED`.
   */
  customPrompt?: string;
  /**
   * The approved template and the host's words for its blanks. `null` clears it. Saving one
   * also clears any free text written before templates.
   */
  template?: { id: string; blanks: CompanionTemplateBlanks } | null;
  adultModeEnabled?: boolean;
  voiceOverrideId?: string | null;
  voiceOverrideProvider?: VoiceOverrideProvider | null;
  /**
   * Host display-name override. `undefined` leaves existing value untouched;
   * `null` or an empty/whitespace-only string clears it so the companion's
   * default name is used again. Max 30 chars after trimming.
   */
  customName?: string | null;
  /**
   * Host body override: an active avatar id. `undefined` leaves it untouched; `null` clears it so
   * the companion's own body is used again.
   */
  bodyAvatarId?: string | null;
}

export interface CompanionPromptOverridePutResponse {
  override: string | null;
  templateId: string | null;
  templateBlanks: CompanionTemplateBlanks | null;
  adultModeEnabled: boolean;
  effectivePrompt: string;
  source: EffectivePromptSource;
  sizeCapBytes: number;
  voiceOverrideId: string | null;
  voiceOverrideProvider: VoiceOverrideProvider | null;
  effectiveVoiceId: string;
  effectiveVoiceProvider: EffectiveVoiceProvider;
  voiceSource: EffectiveVoiceSource;
  bodyOverrideAvatarId: string | null;
  customName: string | null;
  effectiveName: string;
  nameSource: EffectiveNameSource;
  /** `'saved'` when a row exists, `'cleared'` when the row was deleted. */
  action: 'saved' | 'cleared';
}

// ---------------------------------------------------------------------------
// GET /api/rooms/[roomId]/adult-content-acknowledgement
// ---------------------------------------------------------------------------

export interface AdultContentAcknowledgementGetResponse {
  acknowledged: boolean;
  acknowledgedAt: string | null;
  /** `true` when the platform kill-switch disables adult mode globally. */
  adultModeDisabled: boolean;
}

// ---------------------------------------------------------------------------
// PUT /api/rooms/[roomId]/adult-content-acknowledgement
// ---------------------------------------------------------------------------

export interface AdultContentAcknowledgementPutBody {
  acknowledged: boolean;
}

export interface AdultContentAcknowledgementPutResponse {
  acknowledged: boolean;
  acknowledgedAt: string | null;
  /**
   * When `acknowledged` transitions from `true` → `false` the server cascades
   * and clears `adult_mode_enabled` on every override row for this workflow.
   * This number reports how many rows were reset.
   */
  cascadedAdultModeResets: number;
}

// ---------------------------------------------------------------------------
// Common error envelope
// ---------------------------------------------------------------------------

export interface CompanionPromptOverrideErrorResponse {
  error: string;
  code: CompanionPromptOverrideErrorCode;
  /** Present on PROMPT_TOO_LARGE — the platform byte limit. */
  maxBytes?: number;
  /** Present on RATE_LIMITED — seconds remaining on the cooldown. */
  retryAfterSec?: number;
  /** Present on INVALID_TEMPLATE_BLANK — what is wrong, by blank. */
  blanks?: Partial<Record<'personality' | 'subject' | 'mood', string>>;
}
